import type {
  ApiNode,
  Filter,
  FilterDataType,
} from "@/workflow_api/types/workflow";
import type { NodeVariable, Variable } from "../../types";
import type { ScopeState } from "../../data-center/components/scope-selector";
import type { ExpedienteConditionData } from "../components/condition";
import type { LogicalOperator } from "../../if/types";
import type {
  ActionItemType,
  LinkSubActionType,
  ValueColumn,
  ValueInputType,
  FieldExpirationConfig,
  LinkActionType,
  LinkUserEntry,
  LinkExpedientsActionType,
  LinkExpedientEntry,
} from "../types";
import type { EntityRoleType } from "@/schemas/digital-files/entity";
import type { FieldType } from "@/schemas/digital-files";
import { formatDate } from "@/utils/format";
import type { VariableType } from "../../calculated-field/CalculatedFieldOperation/types";
import { NodeTypes } from "@/workflow_api/types/workflow";
import { getValueType } from "../../if/serialization";

/**
 * Expediente Input type for flat input structure
 */
export interface ExpedienteInput {
  name: string;
  type: "STRING" | "INTEGER" | "ARRAY" | "BOOLEAN" | "NUMBER" | "DOCUMENT";
  value: string | number | boolean | null | string[];
  connection?: string;
  field?: string;
  output_name?: string;
  global_type?: Variable["global_type"];
  reference_key?: string;
}

export const getSerializedScope = (scope: ScopeState): Array<Filter> => {
  const condition = scope.conditionScope;
  const isDateType = scope.scope.data_type === "date";
  const comparative = getSerializedVariableValue(
    scope.comparativeValue,
    isDateType,
  );
  const initRange = getSerializedVariableValue(
    scope.initialRange.value,
    isDateType,
    false,
  );
  const endRange = getSerializedVariableValue(
    scope.endRange.value,
    isDateType,
    false,
  );
  const serializedScope: Filter = {
    condition: condition.name ?? "",
    name: scope.scope.id,
    type: (scope.scope.data_type as FilterDataType) ?? "DATE",
    value: condition?.needsComparativeValue
      ? !comparative.isVariable
        ? comparative.value
        : null
      : condition?.needsComparativeRange
        ? JSON.stringify([initRange.value, endRange.value])
        : null,
  };
  if (comparative.isVariable) {
    const variable = scope.comparativeValue as Variable;
    if (variable.global_type) {
      serializedScope.value = variable.node_id || null;
      serializedScope.global_type = variable.global_type;
    } else {
      serializedScope.connection = variable.node_id;
      serializedScope.field = variable.id;
    }
  }

  return [serializedScope];
};

const getSerializedVariableValue = (
  value: string | Variable | null,
  shouldNormalizeDate: boolean = false,
  useNameAsValue: boolean = true,
): {
  isVariable: boolean;
  value: string | null;
  type: VariableType | null;
} => {
  if (value === null) {
    return {
      isVariable: false,
      value: null,
      type: null,
    };
  }

  if (typeof value === "string") {
    return {
      isVariable: false,
      value: shouldNormalizeDate ? formatDate(value) : value,
      type: null,
    };
  }
  return {
    isVariable: true,
    value: useNameAsValue ? (value as Variable).name : (value as Variable).id,
    type: (value as Variable).type,
  };
};

const getFilterType = (
  operationType?: ValueInputType | VariableType,
): FilterDataType => {
  switch (operationType) {
    case "positive-integer":
    case "number":
      return "NUMBER";
    case "boolean":
      return "BOOLEAN";
    case "date":
      return "DATE";
    case "string":
    case "regex":
    default:
      return "STRING";
  }
};

const getFilterValue = (
  value: string | null,
  type: FilterDataType,
): string | number | boolean | null => {
  if (value === null) return null;
  if (type === "NUMBER") {
    return Number(value);
  } else if (type === "BOOLEAN") {
    return value === "true";
  } else {
    return value;
  }
};

export const getSerializedFilters = (
  conditionsData: ExpedienteConditionData[],
): Array<Filter> => {
  return conditionsData.map((condition) => {
    const isDateType = condition.operation?.type === "date";
    const variable = getSerializedVariableValue(condition.variable);
    const comparative = getSerializedVariableValue(
      condition.comparativeValue,
      isDateType,
      false,
    );
    const initRange = getSerializedVariableValue(
      condition.initialRangeValue,
      isDateType,
      false,
    );
    const endRange = getSerializedVariableValue(
      condition.endRangeValue,
      isDateType,
      false,
    );
    const type = getFilterType(
      variable?.type ??
        condition.operation?.comparativeValueType ??
        condition.operation?.type,
    );
    const filter = {
      name: variable.value || "",
      condition: condition.operation?.name ?? "",
      value: condition.operation?.needsComparativeValue
        ? !comparative.isVariable
          ? getFilterValue(comparative.value, type)
          : null
        : condition.operation?.needsComparativeRange
          ? JSON.stringify([initRange.value, endRange.value])
          : null,
      type,
    } as Filter;
    if (comparative.isVariable) {
      const variable = condition.comparativeValue as Variable;
      if (variable.global_type) {
        filter.value = variable.node_id || null;
        filter.global_type = variable.global_type;
      } else {
        filter.connection = variable.node_id;
        filter.field = variable.id;
      }
    }
    return filter;
  });
};

/**
 * Timer config type mapping from internal to API format
 */
type TimerConfigType = "expiration_date" | "expiration_duration";

/**
 * Serialize the field expiration configuration to timer_config format
 *
 * @param expiration The expiration configuration for a field
 * @returns Serialized timer_config object matching API format
 */
const getSerializedTimerConfig = (expiration: FieldExpirationConfig) => {
  // Map internal type to API type
  const apiType: TimerConfigType =
    expiration.type === "expiration_date"
      ? "expiration_date"
      : "expiration_duration";

  // Serialize replacement value (handles both string and Variable types)
  const serializedReplaceValue = getSerializedVariableValue(
    expiration.replacementValue,
    false,
  );
  const replaceValue = serializedReplaceValue.isVariable
    ? `{{${serializedReplaceValue.value}}}`
    : (serializedReplaceValue.value ?? "");

  if (apiType === "expiration_date") {
    // Serialize expiration date (handles both string and Variable types)
    const serializedExpirationDate = getSerializedVariableValue(
      expiration.expirationDate ?? null,
      true,
    );
    const expiresDate = serializedExpirationDate.isVariable
      ? `{{${serializedExpirationDate.value}}}`
      : (serializedExpirationDate.value ?? null);

    // Serialize notification date (handles both string and Variable types)
    const serializedNotificationDate = getSerializedVariableValue(
      expiration.notificationDate ?? null,
      true,
    );
    const notificationDate = serializedNotificationDate.isVariable
      ? `{{${serializedNotificationDate.value}}}`
      : (serializedNotificationDate.value ?? null);

    return {
      type: apiType,
      expires_date: expiresDate,
      notification_date: notificationDate,
      replace_value: replaceValue,
    };
  } else {
    return {
      type: apiType,
      expires_amount: expiration.expirationAmount ?? null,
      expires_unit: expiration.expirationUnit ?? null,
      notification_amount: expiration.notificationAmount ?? null,
      notification_unit: expiration.notificationUnit ?? null,
      replace_value: replaceValue,
    };
  }
};

/**
 * Field entry type for serialization matching API format
 */
interface SerializedField {
  name: string;
  field_type: string;
  is_required: boolean;
  value: string | boolean;
  timer_config?: ReturnType<typeof getSerializedTimerConfig>;
}

/**
 * Serialize the columns data for create and update operations
 * Generates the 'fields' object in the format expected by the API:
 * {
 *   "field-id-1": { name, field_type, is_required, value, timer_config? },
 *   "field-id-2": { ... }
 * }
 *
 * @param columns The value columns from the form
 * @returns Serialized fields as a JSON string in the format expected by the API
 */
export const getSerializedColumns = (columns: ValueColumn[]): string => {
  const fieldsData: Record<string, SerializedField> = {};

  columns.forEach((column) => {
    // Only include columns that have a value or are required
    const serializedValue = getSerializedVariableValue(
      column.value,
      column.valueType === "date",
    );

    let value: string | boolean;
    if (serializedValue.isVariable) {
      // For variables, use the mustache template syntax
      value = `{{${serializedValue.value}}}`;
    } else if (column.valueType === "boolean") {
      // For boolean type, convert string to boolean
      value = column.value === "true";
    } else {
      // For other types, use the string value
      value = serializedValue.value || "";
    }

    const fieldEntry: SerializedField = {
      name: column.label,
      field_type:
        column.fieldType?.toUpperCase() ??
        getFieldTypeFromValueType(column.valueType),
      is_required: column.isRequired,
      value,
    };

    // Add timer_config if expiration is configured
    if (column.expiration) {
      fieldEntry.timer_config = getSerializedTimerConfig(column.expiration);
    }

    // Use column.name (field id) as the key
    fieldsData[column.name] = fieldEntry;
  });

  return JSON.stringify(fieldsData);
};

/**
 * Map ValueInputType to field_type for API
 */
const getFieldTypeFromValueType = (valueType: ValueInputType): string => {
  switch (valueType) {
    case "string":
      return "TEXT";
    case "number":
      return "NUMBER";
    case "date":
      return "DATE";
    case "boolean":
      return "BOOLEAN";
    case "regex":
      return "TEXT";
    case "positive-integer":
      return "NUMBER";
    default:
      return "TEXT";
  }
};

/**
 * Helper to get value from a Variable object for flat input serialization
 * Returns the appropriate input structure based on whether it's a form variable or other node type
 */
const getVariableInputValue = (
  variable: Variable,
  inputVariables?: NodeVariable[],
): {
  value: string | null;
  connection?: string;
  field?: string;
  output_name?: string;
  global_type?: Variable["global_type"];
  type?: ExpedienteInput["type"];
} => {
  // Find the node variable to get more context
  const nodeVar = inputVariables?.find((nv) => nv.nodeId === variable.node_id);

  // For global variables (APPLICANT, CURRENT_TIME, etc.)
  if (variable.global_type) {
    if (
      variable.global_type === "NODE_EXECUTION_START" ||
      variable.global_type === "NODE_EXECUTION_FINISH"
    ) {
      return {
        value: variable.node_id || "",
        global_type: variable.global_type,
        connection: variable.node_id,
      };
    }

    return {
      value: variable.id,
      global_type: variable.global_type,
      type: getValueType(variable?.type || "string") as ExpedienteInput["type"],
    };
  }

  // For form variables
  if (nodeVar?.ref_name === NodeTypes.FORM) {
    // Check if this is an expanded data center select variable (has field_id and output_name)
    if (variable.field_id && variable.output_name) {
      return {
        value: null,
        connection: variable.node_id,
        field: variable.field_id, // Use field_id instead of variable.id
        output_name: variable.output_name,
      };
    }
    return {
      value: null,
      connection: variable.node_id,
      field: variable.id,
    };
  }

  // For interoperability or N8N nodes
  if (
    nodeVar?.type === NodeTypes.INTEROPERABILITY ||
    nodeVar?.type === NodeTypes.N8N
  ) {
    return {
      value: null,
      connection: variable.node_id,
      output_name: variable.name, // Use variable.name instead of variable.id for N8N/Interoperability nodes
    };
  }

  // For other variable types (e.g., from other workflow nodes like expediente digital, campo calculado, etc.)
  // Send both connection and output_name to properly identify the variable
  return {
    value: null,
    connection: variable.node_id,
    output_name: variable.output_name, // Use variable.name to match the field name as returned by the node
  };
};

/**
 * Check if a field should be sent as DOCUMENT type
 * URL fields should be sent as DOCUMENT to properly link documents
 * @param fieldId - The field ID to check
 * @param entityFields - Entity fields to lookup the field_type
 * @returns true if field should be sent as DOCUMENT, false otherwise
 */
const isDocumentField = (
  fieldId: string,
  entityFields?: FieldType[],
): boolean => {
  if (!entityFields) return false;
  const field = entityFields.find((f) => f.id === fieldId);
  const fieldTypeUpper = field?.field_type?.toUpperCase();
  return fieldTypeUpper === "URL";
};

/**
 * Create a flat input for a string/generic value
 * If the value is a Variable, returns connection/field structure
 * @param name - Input name
 * @param value - Input value (string or Variable)
 * @param inputVariables - Available input variables
 * @param isDocumentType - If true, sends as DOCUMENT type instead of STRING (for URL fields)
 */
const createStringInput = (
  name: string,
  value: string | Variable | null,
  inputVariables?: NodeVariable[],
  isDocumentType?: boolean,
  reference_key?: string,
): ExpedienteInput => {
  const inputType = isDocumentType ? "DOCUMENT" : "STRING";

  if (value === null || value === "") {
    return { name, reference_key, type: inputType, value: "" };
  }

  if (typeof value === "object" && "node_id" in value) {
    // It's a Variable
    const varInput = getVariableInputValue(value, inputVariables);
    return {
      name,
      type: inputType,
      reference_key,
      ...varInput,
    };
  }

  // At this point, value must be a string
  return { name, reference_key, type: inputType, value: value as string };
};

/**
 * Create a flat input for an integer value
 */
const createIntegerInput = (
  name: string,
  value: number | string | Variable | null,
  inputVariables?: NodeVariable[],
  reference_key?: string,
): ExpedienteInput => {
  if (value === null || value === "") {
    return { name, type: "INTEGER", value: null, reference_key };
  }

  if (typeof value === "object" && "node_id" in value) {
    const varInput = getVariableInputValue(value, inputVariables);
    return {
      name,
      type: "INTEGER",
      reference_key,
      ...varInput,
    };
  }

  // At this point, value must be a string or number (not Variable)
  const numValue =
    typeof value === "string" ? parseInt(value, 10) : (value as number);
  return { name, type: "INTEGER", value: numValue, reference_key };
};

/**
 * Serialize the node to be saved in the db using flat input structure
 * Expediente Node - New format according to documentation
 *
 * @param nodeType - The action type (create, read, update, deactivate, link_users)
 * @param currentNode - The current API node
 * @param title - Display name for the node
 * @param flowId - The flow ID
 * @param expedientBaseEntityId - ID of the base entity
 * @param expedientRegisterId - ID of the expedient register (or variable reference)
 * @param is_multiple - Whether to process multiple records
 * @param conditions - Filter conditions
 * @param logicalOperator - Logical operator for conditions
 * @param scope - Scope configuration
 * @param outputName - Output variable name
 * @param limitCount - Limit count for records
 * @param columns - Value columns with field data
 * @param linkAction - Link action type (link or unlink)
 * @param linkUsers - Users to link/unlink
 * @param entityRoles - Entity roles for role name resolution
 * @param registryName - Name for the registry
 * @param registryNameInputType - Input type for registry name ('inp' or 'var')
 * @param registryNameVariable - Variable for registry name (if inputType is 'var')
 * @param inputVariables - Available input variables for resolving connections
 * @param linkExpedientsAction - Link expedients action type (link or unlink)
 * @param linkExpedients - Expedients to link/unlink
 * @param entityFields - Entity fields for field_type lookup
 * @returns Update body API node
 */
export const getSerializedNode = (
  nodeType: ActionItemType | LinkSubActionType,
  currentNode: ApiNode,
  title: string,
  flowId: string,
  expedientBaseEntityId: string,
  expedientRegisterId: string,
  _is_multiple: boolean,
  _conditions: ExpedienteConditionData[],
  _logicalOperator: LogicalOperator,
  _scope: ScopeState,
  _outputName: string,
  _limitCount: number,
  columns: ValueColumn[],
  linkAction?: LinkActionType,
  linkUsers?: LinkUserEntry[],
  entityRoles?: EntityRoleType[],
  registryName?: string,
  registryNameInputType?: "inp" | "var",
  registryNameVariable?: Variable | null,
  inputVariables?: NodeVariable[],
  linkExpedientsAction?: LinkExpedientsActionType,
  linkExpedients?: LinkExpedientEntry[],
  entityFields?: FieldType[],
) => {
  const inputs: ExpedienteInput[] = [];

  // Operation input (required for all operations)
  inputs.push({
    name: "operation",
    type: "STRING",
    value: nodeType.toUpperCase(),
  });

  // Expedient base entity ID (required for all operations)
  inputs.push({
    name: "expedient_base_entity_id",
    type: "STRING",
    value: expedientBaseEntityId,
  });

  // Handle expedient_register_id - check if it's a variable reference
  const isRegistryIdVariable =
    expedientRegisterId.startsWith("{{") && expedientRegisterId.endsWith("}}");
  if (isRegistryIdVariable) {
    // Extract variable name and find the variable
    const varName = expedientRegisterId.slice(2, -2);
    const variable = inputVariables
      ?.flatMap((nv) => nv.variables)
      .find((v) => v.id === varName);
    if (variable) {
      inputs.push(
        createStringInput("expedient_register_id", variable, inputVariables),
      );
    } else {
      // Fallback to string value
      inputs.push({
        name: "expedient_register_id",
        type: "STRING",
        value: expedientRegisterId,
      });
    }
  } else if (nodeType !== "create") {
    // For non-create operations, add the registry ID
    inputs.push({
      name: "expedient_register_id",
      type: "STRING",
      value: expedientRegisterId,
    });
  }

  // Handle different operations
  switch (nodeType) {
    case "read":
    case "deactivate":
      // READ and DEACTIVATE only need operation, expedient_base_entity_id, and expedient_register_id
      // These are already added above
      break;

    case "create":
    case "update":
      // Add registry name
      if (registryNameInputType === "var" && registryNameVariable) {
        inputs.push(
          createStringInput(
            "expedient_register_name",
            registryNameVariable,
            inputVariables,
          ),
        );
      } else if (registryName?.trim()) {
        inputs.push({
          name: "expedient_register_name",
          type: "STRING",
          value: registryName.trim(),
        });
      }

      // Add fields with their timers
      if (columns.length > 0) {
        // First, add the array of field IDs
        const fieldIds = columns.map((col) => col.name);
        inputs.push({
          name: "expedient_base_fields_id",
          type: "ARRAY",
          value: fieldIds,
        });

        // Then add each field value with its timer configuration (positional correspondence)
        columns.forEach((column) => {
          // Add field value
          const fieldValue = column.value;

          // Check if this field should be sent as DOCUMENT type (URL fields)
          const shouldSendAsDocument = isDocumentField(
            column.name,
            entityFields,
          );

          // Handle different value scenarios for update action:
          // 1. Variable selected: send variable connection
          // 2. "__DELETE__" marker: send "" (to delete/clear the field value)
          // 3. Input with value: send the value
          // 4. Empty/not configured: send null (backend won't modify)
          if (
            column.inputType === "variable" &&
            typeof fieldValue === "object" &&
            fieldValue !== null
          ) {
            // Variable selected - use DOCUMENT type for URL fields
            inputs.push(
              createStringInput(
                "fields",
                fieldValue as Variable,
                inputVariables,
                shouldSendAsDocument,
                column.name,
              ),
            );
          } else if (fieldValue === "__DELETE__") {
            // Marked for deletion - send empty string to clear the value
            inputs.push({
              name: "fields",
              type: shouldSendAsDocument ? "DOCUMENT" : "STRING",
              value: "",
              reference_key: column.name,
            });
          } else if (
            column.inputType === "input" &&
            typeof fieldValue === "string" &&
            fieldValue.trim() !== ""
          ) {
            // Input with value
            const serializedValue = getSerializedVariableValue(
              fieldValue,
              column.valueType === "date",
            );
            inputs.push({
              name: "fields",
              type: shouldSendAsDocument ? "DOCUMENT" : "STRING",
              value: serializedValue.value || null,
              reference_key: column.name,
            });
          } else {
            // Not configured - send null (backend won't modify this field)
            inputs.push({
              name: "fields",
              type: shouldSendAsDocument ? "DOCUMENT" : "STRING",
              value: null,
              reference_key: column.name,
            });
          }

          // Add timer configuration (always add, use null if not configured)
          const expiration = column.expiration;
          if (expiration) {
            // Timer type
            const timerType =
              expiration.type === "expiration_date"
                ? "expiration_date"
                : "expiration_duration";
            inputs.push({
              name: "timer_type",
              type: "STRING",
              value: timerType,
              reference_key: column.name,
            });

            // Timer expires value
            if (timerType === "expiration_date") {
              // Handle expiration date (can be string or Variable)
              if (
                expiration.expirationDateInputType === "var" &&
                typeof expiration.expirationDate === "object"
              ) {
                inputs.push(
                  createStringInput(
                    "timer_expires_value",
                    expiration.expirationDate as Variable,
                    inputVariables,
                    false,
                    column.name,
                  ),
                );
              } else {
                const serializedExpirationDate = getSerializedVariableValue(
                  expiration.expirationDate ?? null,
                  true,
                );
                inputs.push({
                  name: "timer_expires_value",
                  type: "STRING",
                  value: serializedExpirationDate.value || "",
                  reference_key: column.name,
                });
              }
              inputs.push({
                name: "timer_expires_unit",
                type: "STRING",
                value: null,
                reference_key: column.name,
              });
              // Handle notification date (can be string or Variable)
              if (
                expiration.notificationDateInputType === "var" &&
                typeof expiration.notificationDate === "object"
              ) {
                inputs.push(
                  createStringInput(
                    "timer_notify_value",
                    expiration.notificationDate as Variable,
                    inputVariables,
                    false,
                    column.name,
                  ),
                );
              } else {
                const serializedNotificationDate = getSerializedVariableValue(
                  expiration.notificationDate ?? null,
                  true,
                );
                inputs.push({
                  name: "timer_notify_value",
                  type: "STRING",
                  value: serializedNotificationDate.value || "",
                  reference_key: column.name,
                });
              }
              inputs.push({
                name: "timer_notify_unit",
                type: "STRING",
                value: null,
                reference_key: column.name,
              });
            } else {
              // expiration_duration
              inputs.push(
                createIntegerInput(
                  "timer_expires_value",
                  expiration.expirationAmount ?? null,
                  inputVariables,
                  column.name,
                ),
              );
              inputs.push({
                name: "timer_expires_unit",
                type: "STRING",
                value: expiration.expirationUnit || null,
                reference_key: column.name,
              });
              inputs.push(
                createIntegerInput(
                  "timer_notify_value",
                  expiration.notificationAmount ?? null,
                  inputVariables,
                  column.name,
                ),
              );
              inputs.push({
                name: "timer_notify_unit",
                type: "STRING",
                value: expiration.notificationUnit || null,
                reference_key: column.name,
              });
            }

            // Timer replace value
            if (
              expiration.replacementInputType === "var" &&
              typeof expiration.replacementValue === "object"
            ) {
              inputs.push(
                createStringInput(
                  "timer_replace_value",
                  expiration.replacementValue as Variable,
                  inputVariables,
                  false,
                  column.name,
                ),
              );
            } else {
              inputs.push({
                name: "timer_replace_value",
                type: "STRING",
                value: (expiration.replacementValue as string) || "",
                reference_key: column.name,
              });
            }
          } else {
            // No expiration configured - add null values for timer fields
            inputs.push({
              name: "timer_type",
              type: "STRING",
              value: null,
              reference_key: column.name,
            });
            inputs.push({
              name: "timer_expires_value",
              type: "STRING",
              value: null,
              reference_key: column.name,
            });
            inputs.push({
              name: "timer_expires_unit",
              type: "STRING",
              value: null,
              reference_key: column.name,
            });
            inputs.push({
              name: "timer_notify_value",
              type: "STRING",
              value: null,
              reference_key: column.name,
            });
            inputs.push({
              name: "timer_notify_unit",
              type: "STRING",
              value: null,
              reference_key: column.name,
            });
            inputs.push({
              name: "timer_replace_value",
              type: "STRING",
              value: null,
              reference_key: column.name,
            });
          }
        });
      }
      break;

    case "link_users":
      if (linkAction && linkUsers) {
        // Override operation for LINK/UNLINK
        inputs[0] = {
          name: "operation",
          type: "STRING",
          value: linkAction === "link" ? "LINK_USERS" : "UNLINK_USERS",
        };

        // Add each user with positional correspondence (user_document, user_email, user_role for each)
        linkUsers.forEach((user) => {
          // User document
          if (
            user.documentInputType === "var" &&
            typeof user.document === "object"
          ) {
            inputs.push(
              createStringInput(
                "user_document",
                user.document as Variable,
                inputVariables,
              ),
            );
          } else {
            inputs.push({
              name: "user_document",
              type: "STRING",
              value:
                typeof user.document === "string" ? user.document.trim() : "",
            });
          }

          // User email
          if (user.emailInputType === "var" && typeof user.email === "object") {
            inputs.push(
              createStringInput(
                "user_email",
                user.email as Variable,
                inputVariables,
              ),
            );
          } else {
            inputs.push({
              name: "user_email",
              type: "STRING",
              value: typeof user.email === "string" ? user.email.trim() : "",
            });
          }

          // User role (only for link action)
          if (linkAction === "link") {
            if (
              user.roleInputType === "var" &&
              typeof user.roleId === "object"
            ) {
              inputs.push(
                createStringInput(
                  "user_role",
                  user.roleId as Variable,
                  inputVariables,
                ),
              );
            } else if (typeof user.roleId === "string" && entityRoles) {
              const role = entityRoles.find((r) => r.id === user.roleId);
              inputs.push({
                name: "user_role",
                type: "STRING",
                value: role?.name || "",
              });
            } else {
              inputs.push({ name: "user_role", type: "STRING", value: "" });
            }
          } else {
            // For unlink, still add user_role to maintain structure (can be empty or the role to unlink)
            if (
              user.roleInputType === "var" &&
              typeof user.roleId === "object"
            ) {
              inputs.push(
                createStringInput(
                  "user_role",
                  user.roleId as Variable,
                  inputVariables,
                ),
              );
            } else if (typeof user.roleId === "string" && entityRoles) {
              const role = entityRoles.find((r) => r.id === user.roleId);
              inputs.push({
                name: "user_role",
                type: "STRING",
                value: role?.name || "",
              });
            } else {
              inputs.push({ name: "user_role", type: "STRING", value: "" });
            }
          }
        });
      }
      break;

    case "link_expedients":
      if (linkExpedientsAction && linkExpedients) {
        // Override operation for LINK/UNLINK
        inputs[0] = {
          name: "operation",
          type: "STRING",
          value:
            linkExpedientsAction === "link"
              ? "LINK_EXPEDIENTS"
              : "UNLINK_EXPEDIENTS",
        };

        // Add each expedient document with positional correspondence
        linkExpedients.forEach((expedient) => {
          // Expedient document
          if (
            expedient.inputType === "var" &&
            typeof expedient.expedientDocument === "object"
          ) {
            inputs.push(
              createStringInput(
                "expedient_document",
                expedient.expedientDocument as Variable,
                inputVariables,
              ),
            );
          } else {
            inputs.push({
              name: "expedient_document",
              type: "STRING",
              value:
                typeof expedient.expedientDocument === "string"
                  ? expedient.expedientDocument.trim()
                  : "",
            });
          }
        });
      }
      break;
  }

  const result = {
    node_id: currentNode.id!,
    node: {
      display_name: title,
      flow_id: flowId,
      order: currentNode.order,
      position_x: currentNode.position_x,
      position_y: currentNode.position_y,
      ref_name: currentNode.ref_name,
      type: currentNode.type,
      inputs,
    },
  };

  return result;
};
